import type {ComposerImage} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {ImagePreviewDialog} from '@github-ui/agent-sessions-interface/components/ImagePreviewDialog'
import {RepoPicker} from '@github-ui/agent-sessions-interface/components/RepoPicker'
import {announce} from '@github-ui/aria-live'
import {useUiReadinessWitness} from '../shared/use-ui-readiness-witness'
import {useCopilotResultControls} from '@github-ui/copilot-browser-latency/use-copilot-result-presentation'
import {CopilotClientRecoveryContext} from '@github-ui/copilot-browser-latency/use-copilot-client-recovery'
import type {CopilotResultControls} from '@github-ui/copilot-browser-latency/copilot-result-presentation'
import {useCopilotClientInteractions} from '@github-ui/copilot-browser-latency/use-copilot-client-interactions'
import {
  clientInteractionForEvent,
  clientInteractionForCommand,
  clientInteractionPending,
  clientInteractionAccepted,
  clientInteractionFailed,
  prepareClientInteraction,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {
  copyClientCommand,
  recordClientCommandPreparation,
  type ClientPreparationOptions,
} from '@github-ui/ahp-session-client/client-interaction-observer'
import {observeClientInteraction} from '@github-ui/ahp-relay/relay-command-observation'
import {clearPersistedDraft, readPersistedDraft, usePersistedDraft} from '../shared/use-persisted-draft'
import {
  getActivePendingToolConfirmation,
  getActiveSessionToolConfirmation,
} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ChatState, MessageAttachment, PendingMessage} from '@microsoft/agent-host-protocol'
import {
  EstablishedAhpSessionError,
  type AhpSessionIdentifiers,
  type AhpSpaceSelection,
  type AhpSubmissionMetadata,
  type ContributedInstructionsMetadata,
  type DispatchReceipt,
  type DirectTurnTelemetryContext,
  type EstablishedAhpSession,
  type FirstTurnHandoff,
  type PreparedPrompt,
  type EstablishedAhpPrompt,
  type EstablishedAhpPromptCommandType,
} from '@github-ui/ahp-session-client/contracts'
import {selectPreviousTurnCustomAgent} from '@github-ui/ahp-session-client/custom-agent-selection'
import type {EstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import {HighlightedUserText} from '@github-ui/copilot-code-prettify/render/HighlightedUserText'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {AnchorSide} from '@primer/behaviors'
import {clsx} from 'clsx'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'
import {
  use,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type Dispatch,
  type Ref,
  type RefObject,
  type ReactNode,
  type SetStateAction,
} from 'react'

import {
  collectExplicitAttachmentKeys,
  useComposerAttachments,
  type ComposerAttachmentsController,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-attachments'
import {ImageAttachmentToken} from '@github-ui/copilot-shared-ui/components/ImageAttachmentToken'
import {
  useReferenceActivation,
  type ReferenceActivationHandler,
} from '@github-ui/copilot-shared-ui/contexts/ReferenceActivationContext'
import {
  useComposerReferenceActivation,
  type ComposerActivationTarget,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-reference-activation'
import imageTokenStyles from '@github-ui/copilot-shared-ui/components/ImageAttachmentToken.module.css'
import {
  EstablishedAhpSessionInput,
  type EstablishedAhpSessionInputIntegrations,
  type EstablishedAhpSessionInputProps,
} from '@github-ui/copilot-shared-ui/components/EstablishedAhpSessionInput'
import {
  getAutoTierLabel,
  isAutoModelTier,
  type AutoModelTier,
  type AutoTierPickerConfig,
} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'
import {EstablishedAhpSessionSurface} from '@github-ui/copilot-shared-ui/components/EstablishedAhpSessionSurface'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {
  useStaffDebugCommandDispatch,
  type StaffDebugCommandFeedback,
} from '@github-ui/copilot-shared-ui/hooks/use-staff-debug-command-dispatch'
import type {AhpDebugEvent} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {
  isSubmissionAbandoned,
  SUBMIT_ERROR_MESSAGE,
  type SubmissionFailure,
  userFacingSubmissionMessage,
} from '@github-ui/ahp-session-client/submission-error'
import {useImplicitContextAttachments} from '@github-ui/copilot-shared-ui/references/use-implicit-references'
import {
  implicitAttachmentIcon,
  implicitAttachmentKey,
  implicitAttachmentUrl,
} from '@github-ui/copilot-shared-ui/references/implicit-context'
import {
  ImplicitContextPageContext,
  ImplicitContextPageResolverContext,
} from '@github-ui/copilot-shared-ui/contexts/ImplicitContextPageContext'
import {useImplicitDismissals} from '@github-ui/copilot-shared-ui/contexts/ImplicitDismissalsContext'
import {
  INJECTED_ATTACHMENT_PREFIX,
  useInjectedReferencesActions,
  useInjectedReferencesData,
} from '@github-ui/copilot-shared-ui/contexts/InjectedReferencesContext'
import {useRepoLoader} from '@github-ui/copilot-shared-ui/repos/use-repos'
import {usePasteReferences} from '@github-ui/copilot-shared-ui/attachments/use-paste-references'
import {uploadAcceptAttribute} from '@github-ui/copilot-shared-ui/attachments/process-uploaded-files'
import {useSandboxUpload} from '@github-ui/copilot-shared-ui/hooks/use-sandbox-upload'
import {AttachmentErrorBanner} from '@github-ui/copilot-shared-ui/components/AttachmentErrorBanner'
import {EntitlementQuotaBanner} from '@github-ui/copilot-shared-ui/components/EntitlementQuotaBanner'
import {useEntitlementQuotaNotice} from '@github-ui/copilot-shared-ui/hooks/use-entitlement-quota-notice'
import type {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {ChatReferenceAutocomplete} from '@github-ui/copilot-shared-ui/components/ChatReferenceAutocomplete'
import {FilePickerPanel} from '@github-ui/copilot-shared-ui/components/FilePickerPanel'
import {ComposerFileAttachBridge} from '@github-ui/copilot-shared-ui/hooks/use-composer-file-attach'
import {ComposerAttachmentScopeContext} from '@github-ui/copilot-shared-ui/contexts/ComposerAttachmentScopeContext'
import {isAttachmentInScope} from '@github-ui/copilot-shared-ui/attachments/attachment-scope'
import {createScopedRepositoryLoader} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {
  useModelPicker,
  useReasoningEffortPicker,
  useSpacePicker,
} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {ModelUpdateOptions} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import {isReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {SessionTokenUsageMeter, TokenUsageMeter} from './TokenUsageMeter'
import {usePrettifyComposer} from '@github-ui/copilot-code-prettify/composer/use-prettify-composer'
import {PrettifiedBlocksList} from '@github-ui/copilot-code-prettify/composer/PrettifiedBlocksList'
import styles from './AhpSessionComposer.module.css'
import responsiveStyles from '../shared/responsive-composer.module.css'
import {usePendingAutoSubmit} from './use-pending-auto-submit'
import {usePendingPromptTakeover} from './use-pending-prompt-takeover'
import {EmptyState, suggestionContextForAttachment} from './EmptyState'
import {ChatIcebreakers} from './ChatIcebreakers'
import {SlashCommandAutocomplete} from './SlashCommandAutocomplete'
import {parseShareCommand} from './slash-commands'
import {CopilotAnimation, CopilotAnimationType} from '@github-ui/copilot-animation'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {ensureSessionMode, ensureApprovalMode} from '@github-ui/ahp-session-client/session-mode'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import type {ComposerHandle, EmptyStateMode} from '../props'
import {ChatPromptFrame} from '../shared/ChatPromptFrame'
import {attachmentRepoPickerScopeSubtitle, resolvePickerScopeOwner} from '../task/shared'
import {AUTO_MODEL_ID} from '../task/ModelPicker'
import {
  DEFAULT_AUTO_MODEL_TIER,
  mergeModelSelectionConfig,
  readAutoModelConfigSchema,
  readAutoTierSwitchFailure,
  readEffectiveAutoTier,
  readPromptAutoTier,
  sanitizeAutoTierConfig,
  stripAutoTierFromPrompt,
  supportsAutoTier,
} from '../shared/auto-tier'
import {
  SELECTED_SPACE_ATTACHMENT_ID,
  hasSubmittableAttachments,
  selectedSpaceAttachment,
} from '../spaces/space-attachment'

type EstablishedAhpPromptDispatchKind = Parameters<
  NonNullable<EstablishedAhpSessionInputProps['preparePromptForDispatch']>
>[1]

const EMPTY_CONTRIBUTED_INSTRUCTIONS: ContributedInstructionsMetadata = []
const IMPLICIT_ATTACHMENT_PREFIX = 'implicit-attachment:'
const CONNECTION_ERROR_MESSAGE = 'The active AHP session could not be connected.'
const MODE_UPDATE_ERROR_MESSAGE = 'Your mode could not be updated. Please try again.'

function resolveSupportedEfforts(
  models: ReturnType<typeof useAgentModels>['models'],
  modelId: string | undefined,
): readonly string[] | undefined {
  return models.find(model => model.id === modelId)?.capabilities.supports?.reasoning_effort
}

type AhpPromptModel = NonNullable<PreparedPrompt['model']>
type AhpPromptReasoningEffort = NonNullable<AhpPromptModel['config']>['reasoningEffort']

export function resolveAhpSessionComposerModel({
  selectedModelId,
  selectedReasoningEffort,
  reasoningEffortEnabled,
  preserveDraftModel,
  draftModel,
  autoTierSupported = false,
  selectedAutoTier,
}: {
  selectedModelId?: string
  selectedReasoningEffort?: AhpPromptReasoningEffort
  reasoningEffortEnabled: boolean
  preserveDraftModel: boolean
  draftModel?: AhpPromptModel
  autoTierSupported?: boolean
  selectedAutoTier?: AutoModelTier
}): PreparedPrompt['model'] {
  const modelId = selectedModelId ?? (preserveDraftModel ? draftModel?.id : undefined)
  if (!modelId) return undefined

  const baseModel = draftModel?.id === modelId ? draftModel : undefined
  const model = mergeModelSelectionConfig({
    modelId,
    baseModel,
    updates: {
      reasoningEffort: reasoningEffortEnabled
        ? (selectedReasoningEffort ?? baseModel?.config?.reasoningEffort)
        : undefined,
    },
    removeKeys: reasoningEffortEnabled ? [] : ['reasoningEffort'],
  })

  return sanitizeAutoTierConfig(model, autoTierSupported, selectedAutoTier)
}

interface PendingDraft {
  attachments: ReturnType<ComposerAttachmentsController['snapshot']>
  injectedIds: string[]
  pastedIds: string[]
  implicitAttachments: MessageAttachment[]
  blocks: ReadonlyMap<string, string>
}

interface AutoTierSelectionState {
  key: string
  effectiveTier?: string
  supported: boolean
  selectedTier?: AutoModelTier
  hasLocalSelection: boolean
}

interface PendingAutoTierRequest {
  tier: AutoModelTier
  effectiveTierAtRequest?: string
  sessionKey: string
}

function getAutoTierSessionKey(session: EstablishedAhpSession): string {
  const {taskId, environmentId, sessionId} = session.identity
  return `${taskId ?? ''}:${environmentId}:${sessionId}`
}

function reconcileAutoTierSelection(
  current: AutoTierSelectionState,
  {
    key,
    mode,
    hostStateKnown,
    supported,
    effectiveTier,
  }: {
    key: string
    mode: 'established' | 'new' | 'none'
    hostStateKnown: boolean
    supported: boolean
    effectiveTier?: string
  },
): AutoTierSelectionState {
  const previousEffectiveTier = isAutoModelTier(current.effectiveTier) ? current.effectiveTier : undefined
  const visibleEffectiveTier = isAutoModelTier(effectiveTier) ? effectiveTier : undefined
  let selectedTier = current.selectedTier
  let hasLocalSelection = current.hasLocalSelection

  if (current.key !== key) {
    if (mode === 'new') {
      selectedTier = DEFAULT_AUTO_MODEL_TIER
      hasLocalSelection = false
    } else if (current.key === 'new' && mode === 'established' && current.hasLocalSelection) {
      selectedTier = current.selectedTier
    } else {
      selectedTier = visibleEffectiveTier
      hasLocalSelection = false
    }
  } else if (mode === 'new' && selectedTier === undefined) {
    selectedTier = DEFAULT_AUTO_MODEL_TIER
    hasLocalSelection = false
  }

  if (mode === 'none' || effectiveTier === 'fast' || (mode === 'established' && hostStateKnown && !supported)) {
    selectedTier = undefined
    hasLocalSelection = false
  } else if (
    mode === 'established' &&
    supported &&
    effectiveTier !== current.effectiveTier &&
    (!hasLocalSelection || selectedTier === undefined || selectedTier === previousEffectiveTier)
  ) {
    selectedTier = visibleEffectiveTier
    hasLocalSelection = false
  } else if (
    mode === 'established' &&
    supported &&
    effectiveTier !== current.effectiveTier &&
    selectedTier === visibleEffectiveTier
  ) {
    hasLocalSelection = false
  } else if (mode === 'established' && supported && !current.supported && selectedTier === undefined) {
    selectedTier = visibleEffectiveTier
    hasLocalSelection = false
  }

  if (
    current.key === key &&
    current.effectiveTier === effectiveTier &&
    current.supported === supported &&
    current.selectedTier === selectedTier &&
    current.hasLocalSelection === hasLocalSelection
  ) {
    return current
  }

  return {key, effectiveTier, supported, selectedTier, hasLocalSelection}
}

interface PreparePromptOptions extends ClientPreparationOptions {
  includeDraftAttachments?: boolean
}

export interface AhpSessionComposerProps {
  ahpSession?: AhpSessionIdentifiers
  /** Stable draft key for hosts whose established session is not available yet (e.g. a disconnected task). */
  draftKey?: string
  attachMenuSide?: AnchorSide
  /**
   * Receives the dispatch receipt naming the turn or queue entry the submission
   * produced, so hosts can follow an individual submission rather than only
   * learning that some submission was accepted.
   */
  onSubmitSuccess?: (receipt?: DispatchReceipt) => void
  /** Runs when a submission fails, including a dispatch the server rejected. */
  onSubmitFailure?: () => void
  readOnly?: boolean
  /** Supplied only by Chat hosts with an entitlement provider; task hosts omit it. */
  chatEntitlement?: ReturnType<typeof useEntitlement>
  disabled?: boolean
  /** Keeps draft preparation available while blocking every live-session command. */
  draftOnly?: boolean
  /** ID of status or guidance text that describes the composer textarea. */
  composerDescriptionId?: string
  portalContainerName?: string
  handleRef?: Ref<ComposerHandle>
  emptyState?: EmptyStateMode
  disableAutoFocus?: boolean
  /** Keeps the draft owner mounted while removing the composer from presentation and interaction. */
  hidden?: boolean
  initialSubmission?: EstablishedAhpInitialSubmissionCoordinator
  initialPrompt?: PreparedPrompt
  onInitialPromptDispatched?: () => void
  initialSubmissionOrigin?: 'new' | 'recovery'
  initialSubmissionMetadata?: AhpSubmissionMetadata
  prepareInitialPrompt?: FirstTurnHandoff['preparePrompt']
  initialSelectedSpace?: AhpSpaceSelection
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  onInitialSubmissionStarted?: (prompt: PreparedPrompt) => void
  onInitialSubmissionStopped?: () => void
  onInitialSubmissionSettled?: () => void
  state?: ChatState
  queuedMessages?: readonly PendingMessage[]
  queueReadOnly?: boolean
  sessionConfiguration?: {
    mode?: AgentMode
    approvalMode?: SessionApprovalMode
    modePicker?: ReactNode
    /**
     * Applies a tool-permission slash command. A `true` result means the text was
     * a recognized command that has been handled over the relay, so it must not
     * be dispatched as a chat turn.
     */
    applyPermissionCommand?: (text: string) => Promise<boolean>
    /** Handles web task sharing without invoking the runtime's separate CLI-sync flow. */
    applyShareCommand?: (text: string) => Promise<boolean>
    /**
     * Answers a repository-dependent command (see `parseRepositoryCommand`) when
     * the session has no repository. A `true` result means the command was
     * refused here, before any submission work, so it never reaches the runtime.
     */
    guardRepositoryCommand?: (text: string) => Promise<boolean>
  }
  /**
   * A submission the host dispatched on this surface's behalf — a turn composed
   * on another page and replayed here — that failed. It never travelled through
   * this composer, so it is reported here as though it had: the same banner, and
   * the composed text back in the field.
   */
  submissionFailure?: SubmissionFailure
  /**
   * Per-turn instructions the surrounding page contributes, attached to each
   * submitted prompt. The snapshot lives with the host's chat manager, which is
   * an application package this one must not depend on, so it arrives as a prop.
   */
  contributedInstructions?: ContributedInstructionsMetadata
  directTurnTelemetryContext?: DirectTurnTelemetryContext
  beforeSubmit?: () => Promise<void> | void
  beforeDispatch?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<void> | void
  repository?: EstablishedAhpSessionInputIntegrations['repository']
  /**
   * Owner login that scopes the restriction shield in the `@`-reference and
   * attachment pickers. Takes precedence over the owner otherwise derived from
   * the submission metadata, so callers bound to a repository (like the
   * established task view) can scope pickers without disabling attachment.
   */
  restrictionOwner?: string
  tokenUsage?: {persistedState?: ChatState}
  preserveDraftAgent?: boolean
  preserveDraftModel?: boolean
  initialValue?: string
  onValueChange?: (value: string) => void
  onResumeIntent?: () => void
  onPromptAccepted?: (prompt: string) => void
  failureMessage?: string
  isTokenBasedBillingEnabled?: boolean
  suppressQueuedMessages?: boolean
  takeoverEnabled?: boolean
  pendingPrompt?: EstablishedAhpPrompt
  onPromptRespond?: (
    toolCallId: string,
    response: unknown,
    commandType: EstablishedAhpPromptCommandType,
  ) => boolean | void
  isSubmittingPrompt?: boolean
  onFocusFallback?: () => void
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  reasoningEffortEnabled?: boolean
  /** Enables per-message Auto optimization tiers for One Copilot Chat and Sandbox surfaces. */
  autoTierEnabled?: boolean
  confirmationTestId?: string
  promptTestId?: string
  isStreaming?: boolean
  debugEvents?: readonly AhpDebugEvent[]
  /**
   * Presents the outcome of a staff debug slash command. These commands run on
   * the client and never become turns, so the host decides where the result
   * surfaces; without this the result is only announced to screen readers.
   */
  reportStaffCommandFeedback?: (feedback: StaffDebugCommandFeedback) => void
  presentation?: 'embedded' | 'inline'
}

interface AhpSessionComposerInputProps extends AhpSessionComposerProps {
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  value: string
  setValue: Dispatch<SetStateAction<string>>
  draftId: string | undefined
}

function imagePreview(
  attachments: ComposerAttachmentsController,
  readOnly: boolean,
  activate: ReferenceActivationHandler | null,
): ReactNode | undefined {
  if (attachments.images.length === 0) return undefined
  return (
    <ul className={imageTokenStyles.List} aria-label="Attached images">
      {attachments.images.map((image: ComposerImage) => (
        <ImageAttachmentToken
          key={image.id}
          image={image}
          onRemove={readOnly ? undefined : attachments.removeImage}
          onPreview={(preview, event) => {
            if (!activate?.({kind: 'image', image: {...preview, src: preview.dataUrl}}, event)) {
              attachments.setPreviewImage(preview)
            }
          }}
        />
      ))}
    </ul>
  )
}

/**
 * Chat's active established surface. Draft attachments, references, and their
 * normalized AHP payload are shared with Mission Control; Chat supplies its
 * existing reference, repository, and space lookup services.
 */
function sessionDraftId(identity: {taskId?: string; sessionId: string} | undefined): string | undefined {
  if (!identity?.sessionId) return undefined
  return identity.taskId ? `${identity.taskId}:${identity.sessionId}` : identity.sessionId
}

function createEstablishedSessionHandoff(_identityKey: string) {
  let session: EstablishedAhpSession | undefined
  return {
    get: () => session,
    set: (nextSession: EstablishedAhpSession | undefined) => {
      session = nextSession
    },
  }
}

function AhpSessionComposerInput({
  ahpSession,
  attachMenuSide,
  onSubmitSuccess,
  onSubmitFailure,
  portalContainerName,
  handleRef,
  readOnly = false,
  chatEntitlement,
  disabled = false,
  draftOnly = false,
  composerDescriptionId,
  disableAutoFocus,
  emptyState,
  hidden = false,
  initialSubmission,
  initialPrompt,
  onInitialPromptDispatched,
  initialSubmissionOrigin = 'new',
  initialSubmissionMetadata,
  prepareInitialPrompt,
  initialSelectedSpace,
  onSessionEstablished,
  onInitialSubmissionStarted,
  onInitialSubmissionStopped,
  onInitialSubmissionSettled,
  submissionFailure,
  sessionConfiguration,
  textAreaRef,
  value,
  setValue,
  draftId,
  contributedInstructions = EMPTY_CONTRIBUTED_INSTRUCTIONS,
  directTurnTelemetryContext,
  beforeSubmit,
  beforeDispatch,
  repository,
  restrictionOwner,
  tokenUsage,
  preserveDraftAgent = false,
  preserveDraftModel = false,
  onValueChange,
  onResumeIntent,
  onPromptAccepted,
  failureMessage,
  isTokenBasedBillingEnabled,
  reasoningEffortEnabled = true,
  autoTierEnabled = false,
  isStreaming: isStreamingOverride,
  debugEvents,
  reportStaffCommandFeedback,
  presentation = 'embedded',
}: AhpSessionComposerInputProps) {
  const establishedSession = useOptionalEstablishedAhpSession()
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  const presentationControls = useCopilotResultControls(hidden)
  const recoveryControls = use(CopilotClientRecoveryContext)
  const observeControls = useCallback(
    (controls: CopilotResultControls) => {
      const stopPresentation = presentationControls?.(controls)
      const stopRecovery = hidden ? undefined : recoveryControls?.(controls)
      return () => {
        stopPresentation?.()
        stopRecovery?.()
      }
    },
    [presentationControls, recoveryControls, hidden],
  )
  const onPresentation = presentationControls || (!hidden && recoveryControls) ? observeControls : undefined
  const previousTurns = establishedView.snapshot.state?.turns
  const activeTurnMessage = establishedView.snapshot.state?.activeTurn?.message
  const previousTurnAgent = useMemo(
    () =>
      selectPreviousTurnCustomAgent(establishedView.snapshot.sessionState, {
        turns: previousTurns ?? [],
        activeTurn: activeTurnMessage ? {message: activeTurnMessage} : undefined,
      }),
    [establishedView.snapshot.sessionState, previousTurns, activeTurnMessage],
  )
  const onReadiness = useUiReadinessWitness({
    kind: 'existing',
    session: establishedSession,
    snapshot: establishedView.snapshot,
    readOnly,
    hidden,
  })
  const {notice: chatQuotaNotice, isBlocking: isBlockedByChatQuota} = useEntitlementQuotaNotice({
    kind: 'chat',
    entitlement: chatEntitlement,
  })
  const interactionObserver = useCopilotClientInteractions({
    role: readOnly ? 'read_only' : establishedView.snapshot.passive ? 'passive' : 'writable',
    surface: directTurnTelemetryContext?.surface,
    session_id: establishedSession?.identity.sessionId ?? ahpSession?.sessionId,
    environment_id: establishedSession?.identity.environmentId ?? ahpSession?.environmentId,
    task_id: establishedSession?.identity.taskId ?? ahpSession?.taskId,
    chat_id: establishedView.snapshot.state?.resource,
  })
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>()
  const consumedStaffCommandRef = useRef('')
  const onStaffCommandFeedback = useCallback(
    (feedback: StaffDebugCommandFeedback) => {
      reportStaffCommandFeedback?.(feedback)
      announce(feedback.message, {assertive: feedback.variant === 'danger'})
      // A failed command may have been typed seconds ago, so only hand the text
      // back when the staff member has not started composing something else.
      if (feedback.variant === 'danger') setValue(current => current || consumedStaffCommandRef.current)
    },
    [reportStaffCommandFeedback, setValue],
  )
  const runStaffDebugCommand = useStaffDebugCommandDispatch({
    sessionId: establishedSession?.identity.sessionId ?? ahpSession?.sessionId,
    onFeedback: onStaffCommandFeedback,
    debugEvents,
  })
  const [pendingAutoRecoverySubmission, setPendingAutoRecoverySubmission] = useState(false)
  const attachButtonRef = useRef<HTMLButtonElement>(null)
  const pendingDraftRef = useRef<PendingDraft | null>(null)
  const pendingInitialValueRef = useRef<string | null>(null)
  const submittedDraftRef = useRef<{draftId: string | undefined; text: string} | null>(null)
  const latestRef = useRef({draftId, value, mounted: true})
  useEffect(() => {
    latestRef.current = {draftId, value, mounted: true}
  }, [draftId, value])
  useEffect(() => {
    latestRef.current.mounted = true
    return () => {
      latestRef.current.mounted = false
    }
  }, [])
  const {selectedModelId, onUpdateModel} = useModelPicker()
  const {selectedReasoningEffort, onUpdateReasoningEffort} = useReasoningEffortPicker()
  const {selectedSpace: contextSelectedSpace, onSelectSpace} = useSpacePicker()
  const selectedSpace = contextSelectedSpace ?? initialSelectedSpace
  const selectedSpaceRef = useRef(selectedSpace)
  useLayoutEffect(() => {
    selectedSpaceRef.current = selectedSpace
  }, [selectedSpace])
  // Owner scope for the restriction shield (github-ui#31409). Prefer the
  // explicit owner from the caller, then the sandbox/scope owner, else the
  // bound repository's owner, so the shield surfaces the current owner
  // restriction in both `@`-reference and attachment pickers. Undefined only
  // when there is no owner at all.
  const referenceRestrictionOwner =
    restrictionOwner ?? initialSubmissionMetadata?.scope ?? initialSubmissionMetadata?.repository?.split('/')[0]
  const [hasChangedSpace, setHasChangedSpace] = useState(false)
  const handleSelectSpace = useCallback(
    (space: AhpSpaceSelection | undefined) => {
      setHasChangedSpace(true)
      onSelectSpace(space)
    },
    [onSelectSpace],
  )
  const appliedModeRef = useRef<{session: EstablishedAhpSession; mode: AgentMode; promise: Promise<void>} | undefined>(
    undefined,
  )
  const appliedApprovalModeRef = useRef<
    {session: EstablishedAhpSession; approvalMode: SessionApprovalMode; promise: Promise<void>} | undefined
  >(undefined)
  const pendingAutoInjectedIdsRef = useRef({ids: [] as string[]})
  const pendingAutoSubmissionRef = useRef<symbol | undefined>(undefined)
  const initialPromptRetryRef = useRef<PreparedPrompt | null>(null)
  const initialPromptDispatchedRef = useRef(false)
  const mountedRef = useRef(true)
  const draftOnlyRef = useRef(draftOnly)
  useLayoutEffect(() => {
    draftOnlyRef.current = draftOnly
  }, [draftOnly])
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const sessionIdentityKey = ahpSession
    ? `${ahpSession.taskId}:${ahpSession.environmentId}:${ahpSession.sessionId}:${ahpSession.channel ?? ''}`
    : 'new'
  const autoTierProductEnabled = autoTierEnabled && copilotFeatureFlags.autoModeV2
  const establishedAutoTierSupported =
    autoTierProductEnabled &&
    establishedSession !== undefined &&
    supportsAutoTier(establishedView.sessionState, readAutoModelConfigSchema(establishedView.rootState))
  const newAutoTierSupported =
    autoTierProductEnabled &&
    establishedSession === undefined &&
    initialSubmission !== undefined &&
    initialSubmissionOrigin === 'new'
  const autoTierSupported = establishedAutoTierSupported || newAutoTierSupported
  const effectiveAutoTier = establishedSession ? readEffectiveAutoTier(establishedView.sessionState) : undefined
  const autoTierStateKey = establishedSession ? getAutoTierSessionKey(establishedSession) : sessionIdentityKey
  const autoTierMode = establishedSession ? 'established' : newAutoTierSupported ? 'new' : 'none'
  const [autoTierSelection, setAutoTierSelection] = useState<AutoTierSelectionState>({
    key: autoTierStateKey,
    effectiveTier: effectiveAutoTier,
    supported: autoTierSupported,
    selectedTier: autoTierMode === 'new' ? DEFAULT_AUTO_MODEL_TIER : undefined,
    hasLocalSelection: false,
  })
  const nextAutoTierSelection = reconcileAutoTierSelection(autoTierSelection, {
    key: autoTierStateKey,
    mode: autoTierMode,
    hostStateKnown: establishedView.sessionState !== undefined,
    supported: autoTierSupported,
    effectiveTier: effectiveAutoTier,
  })
  if (nextAutoTierSelection !== autoTierSelection) setAutoTierSelection(nextAutoTierSelection)
  const selectedAutoTier = nextAutoTierSelection.selectedTier
  const [pendingAutoTier, setPendingAutoTier] = useState<PendingAutoTierRequest | undefined>()
  const pendingAutoTierIsCurrent =
    pendingAutoTier?.sessionKey === autoTierStateKey && pendingAutoTier.effectiveTierAtRequest === effectiveAutoTier
  const visiblePendingAutoTier = pendingAutoTier && pendingAutoTierIsCurrent ? pendingAutoTier.tier : undefined
  if (pendingAutoTier && !pendingAutoTierIsCurrent) setPendingAutoTier(undefined)
  const [autoTierError, setAutoTierError] = useState<{message: string; context: string; eventId: string} | undefined>()
  const autoTierErrorContext = `${autoTierStateKey}:${effectiveAutoTier ?? ''}:${selectedModelId ?? ''}`
  if (autoTierError && autoTierError.context !== autoTierErrorContext) setAutoTierError(undefined)
  const observedAutoTierFailureRef = useRef<{key: string; eventId?: string} | undefined>(undefined)
  const autoTierFailure = readAutoTierSwitchFailure(establishedView.sessionState)
  useEffect(() => {
    if (!establishedView.sessionState || !autoTierProductEnabled) return

    const observed = observedAutoTierFailureRef.current
    if (!observed || observed.key !== autoTierStateKey) {
      observedAutoTierFailureRef.current = {key: autoTierStateKey, eventId: autoTierFailure?.eventId}
      return
    }
    if (!autoTierFailure || observed.eventId === autoTierFailure.eventId) return

    observed.eventId = autoTierFailure.eventId
    const requestedLabel = isAutoModelTier(autoTierFailure.requestedAutoTier)
      ? getAutoTierLabel(autoTierFailure.requestedAutoTier)
      : 'the selected optimization'
    const reason = autoTierFailure.reason.trim()
    setPendingAutoTier(undefined)
    setAutoTierError({
      message: `Could not apply ${requestedLabel}.${reason ? ` ${reason}` : ''}`,
      context: autoTierErrorContext,
      eventId: autoTierFailure.eventId,
    })
  }, [autoTierErrorContext, autoTierFailure, autoTierProductEnabled, autoTierStateKey, establishedView.sessionState])
  const establishedSessionHandoff = useMemo(
    () => createEstablishedSessionHandoff(sessionIdentityKey),
    [sessionIdentityKey],
  )
  useEffect(() => {
    establishedSessionHandoff.set(establishedSession)
  }, [establishedSession, establishedSessionHandoff])
  const publishEstablishedSession = useCallback(
    (session: EstablishedAhpSession) => {
      establishedSessionHandoff.set(session)
      onSessionEstablished?.(session)
    },
    [establishedSessionHandoff, onSessionEstablished],
  )
  // Read at submit time rather than captured: the handler's identity changes with
  // the session's reported mode, and resubscribing the submit callback on every
  // mode change would churn every consumer that holds onto it.
  const applyPermissionCommandRef = useRef(sessionConfiguration?.applyPermissionCommand)
  const applyShareCommandRef = useRef(sessionConfiguration?.applyShareCommand)
  const guardRepositoryCommandRef = useRef(sessionConfiguration?.guardRepositoryCommand)
  useEffect(() => {
    applyPermissionCommandRef.current = sessionConfiguration?.applyPermissionCommand
    applyShareCommandRef.current = sessionConfiguration?.applyShareCommand
    guardRepositoryCommandRef.current = sessionConfiguration?.guardRepositoryCommand
  }, [
    sessionConfiguration?.applyPermissionCommand,
    sessionConfiguration?.applyShareCommand,
    sessionConfiguration?.guardRepositoryCommand,
  ])

  const consumePrompt = useCallback(
    async (_session: EstablishedAhpSession, prompt: PreparedPrompt): Promise<boolean> =>
      (await applyPermissionCommandRef.current?.(prompt.text)) ?? false,
    [],
  )
  // Sharing applies to the host's web task rather than to the agent session, and
  // a refused repository command needs no session either, so both are handled
  // before any submission work and never prepare, provision, or reach a session.
  // They are read from the text as typed, since a prepared prompt starts with any
  // pasted code blocks.
  const isSessionlessCommand = useCallback(
    (text: string) => Boolean(sessionConfiguration?.applyShareCommand) && parseShareCommand(text) !== null,
    [sessionConfiguration?.applyShareCommand],
  )
  const consumeSessionlessPrompt = useCallback(
    async (text: string): Promise<boolean> => {
      if (isSessionlessCommand(text)) return (await applyShareCommandRef.current?.(text)) ?? false
      return (await guardRepositoryCommandRef.current?.(text)) ?? false
    },
    [isSessionlessCommand],
  )
  // A command typed into the composer prepares no draft, so it clears only its
  // own text and leaves the draft's attachments and code blocks for the next
  // message. Like an ordinary submit, the command can settle after the user
  // switched sessions or typed more, so it clears only a draft still showing it.
  const consumeSessionlessDraft = useCallback(
    async (text: string): Promise<boolean> => {
      const submitted = {draftId: latestRef.current.draftId, value: latestRef.current.value}
      if (!(await consumeSessionlessPrompt(text))) return false
      const latest = latestRef.current
      if (latest.mounted && latest.draftId === submitted.draftId && latest.value === submitted.value) {
        setValue('')
        onValueChange?.('')
      } else if (submitted.draftId) {
        clearPersistedDraft(submitted.draftId, 'chat', submitted.value)
      }
      return true
    },
    [consumeSessionlessPrompt, onValueChange, setValue],
  )

  const configureSession = useCallback(
    async (session: EstablishedAhpSession): Promise<void> => {
      const mode = sessionConfiguration?.mode
      if (mode) {
        const applied = appliedModeRef.current
        if (applied?.session === session && applied.mode === mode) {
          await applied.promise
        } else {
          // A session created with `config.mode` already reports it, so the first apply
          // can skip the write. After that, an earlier write may still be pending and the
          // host snapshot can be stale, so always write the new selection.
          const promise =
            applied?.session === session ? session.commands.setMode(mode) : ensureSessionMode(session, mode)
          appliedModeRef.current = {session, mode, promise}
          try {
            await promise
          } catch (error) {
            if (appliedModeRef.current?.promise === promise) appliedModeRef.current = undefined
            throw error
          }
        }
      }

      const approvalMode = sessionConfiguration?.approvalMode
      if (approvalMode) {
        const applied = appliedApprovalModeRef.current
        if (applied?.session === session && applied.approvalMode === approvalMode) {
          await applied.promise
        } else {
          // A session created with `config.approvalMode` already reports it, so
          // the first apply can skip the write. After that, an earlier write may
          // still be pending and the host snapshot can be stale, so always write
          // the new selection.
          const promise = (async () => {
            try {
              if (applied?.session === session) {
                await session.commands.setApprovalMode(approvalMode)
              } else {
                await ensureApprovalMode(session, approvalMode)
              }
            } catch {
              await session.commands.setApproveAll(false)
            }
          })()
          appliedApprovalModeRef.current = {session, approvalMode, promise}
          try {
            await promise
          } catch (error) {
            if (appliedApprovalModeRef.current?.promise === promise) appliedApprovalModeRef.current = undefined
            throw error
          }
        }
      }
    },
    [sessionConfiguration],
  )

  useEffect(() => {
    if (draftOnly || !establishedSession || (!sessionConfiguration?.mode && !sessionConfiguration?.approvalMode)) return
    const applyMode = async () => {
      try {
        await configureSession(establishedSession)
      } catch {
        setSubmitErrorMessage(MODE_UPDATE_ERROR_MESSAGE)
        announce(MODE_UPDATE_ERROR_MESSAGE, {assertive: true})
      }
    }
    void applyMode()
  }, [configureSession, draftOnly, establishedSession, sessionConfiguration?.approvalMode, sessionConfiguration?.mode])
  // An error that opts in to being shown explains a recovery the others cannot
  // (e.g. a handed-off turn too large to store, where the fix is to remove an
  // attachment), so it wins. A failed reconnect is named next, and anything else
  // stays generic so raw failures are never rendered.
  const handleSubmitError = useCallback(
    (error?: unknown) => {
      pendingDraftRef.current = null
      setPendingAutoTier(undefined)
      if (pendingInitialValueRef.current !== null) {
        setValue(pendingInitialValueRef.current)
        pendingInitialValueRef.current = null
      }
      const message =
        userFacingSubmissionMessage(error) ??
        (!establishedSession &&
        initialSubmissionOrigin === 'recovery' &&
        error instanceof EstablishedAhpSessionError &&
        (error.operation === 'establish' || error.operation === 'start')
          ? CONNECTION_ERROR_MESSAGE
          : (failureMessage ?? SUBMIT_ERROR_MESSAGE))
      setSubmitErrorMessage(message)
      announce(message, {assertive: true})
      onSubmitFailure?.()
    },
    [establishedSession, failureMessage, initialSubmissionOrigin, onSubmitFailure, setValue],
  )
  // Editing clears the banner, but a resubmit need not involve editing: a failed
  // turn is restored into the field, so pressing Send again on the same text left
  // the previous failure sitting above an attempt that had not failed yet.
  const clearSubmitError = useCallback(() => setSubmitErrorMessage(undefined), [])
  // A turn the host submitted on this surface's behalf never reached the submit
  // path above, so its failure would otherwise go unreported — leaving an empty
  // conversation and no sign that the turn was lost. Reported once per failure,
  // with the composed text restored so the user can resend it.
  const reportedFailureRef = useRef<SubmissionFailure | null>(null)
  useEffect(() => {
    if (!submissionFailure || reportedFailureRef.current === submissionFailure) return
    reportedFailureRef.current = submissionFailure
    // Never over content already in the field: the restore is a recovery, not an
    // overwrite, so anything the composer already holds wins.
    setValue(current => (current.trim().length > 0 ? current : submissionFailure.text))
    setSubmitErrorMessage(submissionFailure.message)
    announce(submissionFailure.message, {assertive: true})
  }, [setValue, submissionFailure])
  const {models, isLoading: isLoadingModels} = useAgentModels()
  const supportedReasoningEfforts = resolveSupportedEfforts(models, selectedModelId)
  const handleModelUpdate = useCallback(
    (modelId: string, options?: ModelUpdateOptions) => {
      onUpdateModel(modelId, resolveSupportedEfforts(models, modelId), options)
    },
    [models, onUpdateModel],
  )
  const model = useMemo(() => {
    return resolveAhpSessionComposerModel({
      selectedModelId,
      selectedReasoningEffort,
      reasoningEffortEnabled,
      preserveDraftModel,
      draftModel: establishedView.snapshot.state?.draft?.model,
      autoTierSupported,
      selectedAutoTier,
    })
  }, [
    autoTierSupported,
    establishedView.snapshot.state?.draft?.model,
    preserveDraftModel,
    reasoningEffortEnabled,
    selectedAutoTier,
    selectedModelId,
    selectedReasoningEffort,
  ])
  const restorePromptModel = useCallback(
    (prompt: PreparedPrompt) => {
      if (!prompt.model) return

      const supportedEfforts = resolveSupportedEfforts(models, prompt.model.id)
      const reasoningEffort = prompt.model.config?.reasoningEffort
      onUpdateModel(prompt.model.id, supportedEfforts, {reconcile: true})
      onUpdateReasoningEffort(isReasoningEffort(reasoningEffort) ? reasoningEffort : undefined, supportedEfforts, {
        reconcile: true,
      })
      const autoTier = prompt.model.id === AUTO_MODEL_ID ? prompt.model.config?.autoTier : undefined
      if (autoTierSupported && isAutoModelTier(autoTier)) {
        setAutoTierSelection(current => ({...current, selectedTier: autoTier, hasLocalSelection: true}))
      }
    },
    [autoTierSupported, models, onUpdateModel, onUpdateReasoningEffort, setAutoTierSelection],
  )
  const handleAutoTierSelected = useCallback(
    (tier: AutoModelTier) => {
      setAutoTierSelection(current => ({...current, selectedTier: tier, hasLocalSelection: true}))
      setAutoTierError(undefined)
    },
    [setAutoTierError, setAutoTierSelection],
  )
  const preparePromptForDispatch = useCallback((prompt: PreparedPrompt, kind: EstablishedAhpPromptDispatchKind) => {
    if (kind === 'queue' || kind === 'steer') return stripAutoTierFromPrompt(prompt)
    if (kind !== 'initial') return prompt
    const promptForDispatch = {...prompt}
    delete promptForDispatch.displaySpace
    if (selectedSpaceRef.current) promptForDispatch.displaySpace = selectedSpaceRef.current
    return copyClientCommand(prompt, promptForDispatch)
  }, [])
  const handleAutoTierDispatchAccepted = useCallback(
    (prompt: PreparedPrompt, kind: EstablishedAhpPromptDispatchKind) => {
      if (kind === 'queue' || kind === 'steer') return
      const requestedAutoTier = readPromptAutoTier(prompt)
      const requestSession = establishedSessionHandoff.get() ?? establishedSession
      const effectiveTierAtRequest = requestSession
        ? readEffectiveAutoTier(requestSession.view.getSnapshot().sessionState)
        : effectiveAutoTier
      if (!requestedAutoTier || requestedAutoTier === effectiveTierAtRequest) {
        setPendingAutoTier(undefined)
        return
      }
      setPendingAutoTier({
        tier: requestedAutoTier,
        effectiveTierAtRequest,
        sessionKey: requestSession ? getAutoTierSessionKey(requestSession) : autoTierStateKey,
      })
    },
    [autoTierStateKey, effectiveAutoTier, establishedSession, establishedSessionHandoff],
  )
  const scopeOwner = use(ComposerAttachmentScopeContext)
  const scopeRevision = useMemo(() => ({owner: scopeOwner}), [scopeOwner])
  const activeScopeRef = useRef(scopeRevision)
  useLayoutEffect(() => {
    activeScopeRef.current = scopeRevision
  }, [scopeRevision])
  // The pickers offer repositories the session can reach, which is narrower than
  // what an attachment may keep: a repo-scoped session should only offer repos
  // owned by its repository's owner, while a link pasted from another owner
  // stays attached.
  const pickerScopeOwner = resolvePickerScopeOwner(scopeOwner, referenceRestrictionOwner)
  const baseRepositoryLoader = useRepoLoader(pickerScopeOwner)
  const repositoryLoader = useMemo(
    () => createScopedRepositoryLoader(baseRepositoryLoader, pickerScopeOwner),
    [baseRepositoryLoader, pickerScopeOwner],
  )
  const rejectionBannerRef = useRef<HTMLElement | null>(null)
  const focusComposer = useCallback(() => textAreaRef.current?.focus(), [textAreaRef])
  const prepareInitialSubmission =
    !establishedSession && initialSubmissionOrigin === 'recovery' ? initialSubmission?.prepare : undefined
  const sandboxUpload = useSandboxUpload({
    taskId: establishedSession?.identity.taskId ?? ahpSession?.taskId,
    sessionId: establishedSession?.identity.sessionId ?? ahpSession?.sessionId,
  })
  const passiveView = establishedView.passive
  const onAttach = useCallback(() => {
    prepareInitialSubmission?.()
    if (!readOnly && !passiveView) onResumeIntent?.()
  }, [prepareInitialSubmission, readOnly, passiveView, onResumeIntent])
  const commonAttachments = useComposerAttachments({
    announceAnchorRef: textAreaRef,
    rejectionBannerRef,
    focusComposer,
    onAttach,
    scopeOwner,
    sandboxUpload,
  })
  // A turn must not reference a sandbox file that has not finished landing on disk,
  // so an in-flight write blocks submit exactly like reading a file to inline does.
  const attachmentsBusy = commonAttachments.isUploading || commonAttachments.uploadsInFlight
  const submissionMetadata = useMemo(() => {
    const metadata = {...initialSubmissionMetadata}
    if (hasChangedSpace) delete metadata.space
    if (selectedSpace) metadata.space = {owner: selectedSpace.owner, slug: selectedSpace.slug}
    return metadata
  }, [initialSubmissionMetadata, hasChangedSpace, selectedSpace])
  const selectedRepository = commonAttachments.selectedRepos[0]?.name
  const draftSubmissionMetadata = useMemo(() => {
    if (!selectedRepository) return submissionMetadata
    const metadata = {...submissionMetadata, selectedRepository}
    if (selectedRepository !== submissionMetadata.selectedRepository && 'selectedBranch' in metadata) {
      delete metadata.selectedBranch
    }
    return metadata
  }, [selectedRepository, submissionMetadata])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const triggerUpload = useCallback(() => fileInputRef.current?.click(), [])
  const implicitPage = use(ImplicitContextPageContext)
  const getLatestImplicitPage = use(ImplicitContextPageResolverContext)
  const {attachments: implicitAttachments, resolveAttachments: resolveImplicitAttachments} =
    useImplicitContextAttachments(implicitPage, getLatestImplicitPage ?? undefined)
  const {injected: allInjected, skipImplicitReferences} = useInjectedReferencesData()
  const injected = useMemo(
    () => allInjected.filter(item => isAttachmentInScope(item.ahp, scopeOwner)),
    [allInjected, scopeOwner],
  )
  const {removeInjected, clearInjected} = useInjectedReferencesActions()
  // Shared with the new-session composer, which composes the first turn of this
  // same conversation on a surface that is unmounted the moment that turn is
  // accepted. Reading its decisions here is what stops a page the user already
  // dropped, or that the first turn already carried, from coming back.
  const {
    dismissed: dismissedImplicit,
    peek: peekDismissedImplicit,
    dismiss: dismissImplicitAttachments,
  } = useImplicitDismissals()
  const {
    blocks,
    hasBlocks,
    handlePaste,
    popLastBlock,
    serialize,
    removeSubmittedBlocks,
    onRemoveBlock,
    onToggleCollapse,
    onCodeChange,
  } = usePrettifyComposer({draftKey: draftId === undefined ? undefined : `chat:${draftId}`})

  const activeImplicitWithoutPasted = useMemo(() => {
    if (skipImplicitReferences) return []
    const explicitKeys = collectExplicitAttachmentKeys(
      commonAttachments.selectedRepos,
      commonAttachments.tracked,
      injected,
      [],
    )
    return implicitAttachments.filter(attachment => {
      const key = implicitAttachmentKey(attachment)
      return isAttachmentInScope(attachment, scopeOwner) && !dismissedImplicit.has(key) && !explicitKeys.has(key)
    })
  }, [
    commonAttachments.selectedRepos,
    commonAttachments.tracked,
    dismissedImplicit,
    implicitAttachments,
    injected,
    skipImplicitReferences,
    scopeOwner,
  ])
  const {
    pastedReferences: allPastedReferences,
    tryAttachReference,
    removePastedReference,
    clearPastedReferences,
    selectSubmittedPastedReferences,
    attachReferenceUrl,
    reconcileFromText,
  } = usePasteReferences({activeImplicitAttachments: activeImplicitWithoutPasted, scopeOwner})
  const pastedReferences = useMemo(
    () => allPastedReferences.filter(item => isAttachmentInScope(item.ahp, scopeOwner)),
    [allPastedReferences, scopeOwner],
  )

  const implicitChips = useMemo(
    () =>
      activeImplicitWithoutPasted.map(attachment => ({
        id: `${IMPLICIT_ATTACHMENT_PREFIX}${implicitAttachmentKey(attachment)}`,
        label: attachment.label ?? '',
        icon: implicitAttachmentIcon(attachment),
        url: implicitAttachmentUrl(attachment),
      })),
    [activeImplicitWithoutPasted],
  )
  const spaceToken = useMemo(() => {
    const token = selectedSpaceAttachment(selectedSpace)
    return token ? {...token, removable: !establishedSession} : undefined
  }, [selectedSpace, establishedSession])
  const composerAttachments = useMemo(
    () => [
      ...(spaceToken ? [spaceToken] : []),
      ...commonAttachments.attachments,
      ...implicitChips,
      ...injected.map(item => item.token),
      ...pastedReferences.map(item => item.token),
    ],
    [spaceToken, commonAttachments.attachments, implicitChips, injected, pastedReferences],
  )
  const activateReference = useReferenceActivation()
  const activationTargets = useMemo<ComposerActivationTarget[]>(
    () => [
      ...activeImplicitWithoutPasted.map(attachment => ({
        id: `${IMPLICIT_ATTACHMENT_PREFIX}${implicitAttachmentKey(attachment)}`,
        target: {kind: 'attachment' as const, attachment},
      })),
      ...[...injected, ...pastedReferences].map(item => ({
        id: item.token.id,
        target: {kind: 'attachment' as const, attachment: item.ahp, id: item.token.id},
      })),
      ...(selectedSpace
        ? [
            {
              id: SELECTED_SPACE_ATTACHMENT_ID,
              target: {kind: 'space' as const, owner: selectedSpace.owner, number: selectedSpace.id},
            },
          ]
        : []),
    ],
    [activeImplicitWithoutPasted, injected, pastedReferences, selectedSpace],
  )
  const referenceActivation = useComposerReferenceActivation(commonAttachments, composerAttachments, activationTargets)

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id === SELECTED_SPACE_ATTACHMENT_ID) {
        // The provisioning context is fixed once a session exists, which is why
        // the Space picker locks then. The token's remove button must not become
        // a second way to clear a selection the picker no longer lets you change.
        if (!establishedSession) handleSelectSpace(undefined)
      } else if (id.startsWith(IMPLICIT_ATTACHMENT_PREFIX)) {
        dismissImplicitAttachments([id.slice(IMPLICIT_ATTACHMENT_PREFIX.length)])
      } else if (id.startsWith(INJECTED_ATTACHMENT_PREFIX)) {
        removeInjected(id)
      } else if (pastedReferences.some(item => item.token.id === id)) {
        removePastedReference(id)
      } else {
        commonAttachments.handleRemoveAttachment(id)
      }
    },
    [
      establishedSession,
      handleSelectSpace,
      commonAttachments,
      dismissImplicitAttachments,
      pastedReferences,
      removeInjected,
      removePastedReference,
    ],
  )

  const handleComposerPaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      handlePaste(event)
      if (event.defaultPrevented) {
        onAttach()
      } else {
        tryAttachReference(event)
      }
    },
    [handlePaste, onAttach, tryAttachReference],
  )

  const preparePrompt = useCallback(
    async (text: string, options: PreparePromptOptions = {}): Promise<PreparedPrompt> => {
      const includeDraftAttachments = options.includeDraftAttachments !== false
      const retryPrompt =
        includeDraftAttachments && initialPromptRetryRef.current ? {...initialPromptRetryRef.current} : null
      if (retryPrompt) {
        delete retryPrompt.displaySpace
        if (selectedSpace) retryPrompt.displaySpace = selectedSpace
      }
      const attachmentSnapshot = includeDraftAttachments ? commonAttachments.snapshot() : undefined
      const submittedInjected = injected
      const submittedPasted = includeDraftAttachments ? pastedReferences : []
      if (options.interaction)
        observeClientInteraction(() => options.interaction?.dependencyChanged('attachments', true))
      const resolvedImplicit = await resolveImplicitAttachments()
      if (options.interaction)
        observeClientInteraction(() => options.interaction?.dependencyChanged('attachments', false))
      if (activeScopeRef.current !== scopeRevision)
        throw new Error('The conversation owner changed. Please submit again.')
      const activeSubmittedPasted = selectSubmittedPastedReferences(submittedPasted)
      const explicitKeys = collectExplicitAttachmentKeys(
        attachmentSnapshot?.repos ?? [],
        attachmentSnapshot?.files ?? [],
        submittedInjected,
        activeSubmittedPasted,
      )
      const activeImplicit = (skipImplicitReferences ? [] : resolvedImplicit).filter(attachment => {
        const key = implicitAttachmentKey(attachment)
        return !peekDismissedImplicit().has(key) && !explicitKeys.has(key)
      })
      const submittedBlocks = new Map(blocks.map(block => [block.id, block.code] as const))
      submittedDraftRef.current = {draftId: latestRef.current.draftId, text: latestRef.current.value}
      if (includeDraftAttachments && attachmentSnapshot) {
        pendingDraftRef.current = {
          attachments: attachmentSnapshot,
          injectedIds: submittedInjected.map(item => item.token.id),
          pastedIds: activeSubmittedPasted.map(item => item.token.id),
          implicitAttachments: resolvedImplicit,
          blocks: submittedBlocks,
        }
      }
      const intendedAttachments = [
        ...(retryPrompt?.attachments ?? []),
        ...activeImplicit,
        ...(attachmentSnapshot?.attachments ?? []),
        ...submittedInjected.map(item => item.ahp),
        ...activeSubmittedPasted.map(item => item.ahp),
      ]
      const attachments = intendedAttachments.filter(attachment => isAttachmentInScope(attachment, scopeOwner))
      const agent =
        (preserveDraftAgent ? establishedView.snapshot.state?.draft?.agent : undefined) ??
        retryPrompt?.agent ??
        previousTurnAgent
      return recordClientCommandPreparation(
        {
          ...retryPrompt,
          text: includeDraftAttachments ? serialize(text) : text,
          attachments: attachments.length > 0 ? attachments : undefined,
          ...(model ? {model} : retryPrompt?.model ? {model: retryPrompt.model} : {}),
          ...(agent ? {agent} : {}),
          ...(contributedInstructions.length > 0
            ? {
                contributedInstructions: contributedInstructions.map(({kind, label, ownerUrl}) => ({
                  kind,
                  label,
                  ...(ownerUrl ? {ownerUrl} : {}),
                })),
              }
            : {}),
        },
        attachments.length === intendedAttachments.length,
      )
    },
    [
      blocks,
      scopeOwner,
      scopeRevision,
      commonAttachments,
      peekDismissedImplicit,
      contributedInstructions,
      injected,
      pastedReferences,
      resolveImplicitAttachments,
      selectSubmittedPastedReferences,
      serialize,
      skipImplicitReferences,
      model,
      preserveDraftAgent,
      establishedView.snapshot.state?.draft?.agent,
      previousTurnAgent,
      selectedSpace,
    ],
  )

  const settleAccepted = useCallback(
    (receipt?: DispatchReceipt) => {
      const submitted = submittedDraftRef.current
      submittedDraftRef.current = null
      const acceptedPrompt = submitted?.text ?? value
      const draft = pendingDraftRef.current
      if (!draft && pendingInitialValueRef.current === null) return
      pendingDraftRef.current = null
      pendingInitialValueRef.current = null
      initialPromptRetryRef.current = null
      if (draft) {
        draft.attachments.clear()
        clearInjected(draft.injectedIds)
        clearPastedReferences(draft.pastedIds)
        dismissImplicitAttachments(draft.implicitAttachments.map(attachment => implicitAttachmentKey(attachment)))
        removeSubmittedBlocks(draft.blocks)
      }
      setSubmitErrorMessage(undefined)
      // The submit may settle after the user switched sessions, left, or typed more.
      // Only clear the box when it still shows the submitted draft; otherwise clear just that stored draft.
      const latest = latestRef.current
      const isSubmittedDraftCurrent =
        latest.mounted && (!submitted || (latest.draftId === submitted.draftId && latest.value === submitted.text))
      if (isSubmittedDraftCurrent) {
        setValue('')
        onValueChange?.('')
      } else if (submitted?.draftId) {
        clearPersistedDraft(submitted.draftId, 'chat', submitted.text)
      }
      onPromptAccepted?.(acceptedPrompt)
      if (establishedSession) onSubmitSuccess?.(receipt)
    },
    [
      clearInjected,
      clearPastedReferences,
      dismissImplicitAttachments,
      establishedSession,
      onSubmitSuccess,
      onPromptAccepted,
      onValueChange,
      removeSubmittedBlocks,
      setValue,
      value,
    ],
  )

  // An abandoned submission is not a failure: the turn was accepted and handed
  // off to another surface, and the user has come back to this one. It settles
  // exactly like a successful submit — consuming the draft's attachments,
  // references and blocks — because leaving them behind would let the same
  // content be sent a second time.
  const handleSubmissionSettled = useCallback(
    (error?: unknown) => {
      if (isSubmissionAbandoned(error)) {
        settleAccepted()
        return
      }
      handleSubmitError(error)
    },
    [handleSubmitError, settleAccepted],
  )

  const initialSessionMode = sessionConfiguration?.mode
  const initialSessionApprovalMode = sessionConfiguration?.approvalMode
  const submitPreparedPrompt = useCallback(
    async (
      preparedPrompt: PreparedPrompt,
      includeDraftAttachments: boolean,
      injectedIds: string[],
    ): Promise<boolean> => {
      const interaction = clientInteractionForCommand(preparedPrompt)
      if (draftOnly || draftOnlyRef.current || isBlockedByChatQuota) {
        interaction?.unresolved('cancelled')
        return false
      }
      const prompt = copyClientCommand(preparedPrompt, {
        ...preparedPrompt,
        ...(preparedPrompt.attachments
          ? {attachments: preparedPrompt.attachments.filter(attachment => isAttachmentInScope(attachment, scopeOwner))}
          : {}),
      })
      const isInitialSubmission = !establishedSession && Boolean(initialSubmission)
      const pendingAutoSubmission = isInitialSubmission ? Symbol() : undefined
      if (pendingAutoSubmission) {
        pendingAutoSubmissionRef.current = pendingAutoSubmission
        pendingAutoInjectedIdsRef.current.ids = injectedIds
      }
      const submit = async (): Promise<boolean> => {
        // The receipt names the turn this dispatch started, when there is one. It is
        // carried to the acceptance callbacks so callers can tie the turn back to
        // this submission. Steering below is only reached while a turn is active, so
        // that receipt carries a queued message id and names no turn.
        let dispatchReceipt: DispatchReceipt | undefined
        let dispatchedPrompt: PreparedPrompt | undefined
        let dispatchKind: EstablishedAhpPromptDispatchKind | undefined
        if (!prompt.text.trim() && !(prompt.attachments && prompt.attachments.length > 0)) {
          interaction?.failed('payload_mismatch')
          return false
        }
        if (draftOnlyRef.current) return false
        clearSubmitError()
        const activeSession = establishedSessionHandoff.get() ?? establishedSession
        if (await consumeSessionlessPrompt(prompt.text)) {
          if (draftOnlyRef.current) return false
        } else if (activeSession) {
          await configureSession(activeSession)
          if (activeScopeRef.current !== scopeRevision) {
            throw new Error('The conversation owner changed. Please submit again.')
          }
          if (draftOnlyRef.current) return false
          // A permission command configures the session over the relay. It is not
          // conversation, so it is applied and consumed here rather than being
          // dispatched as a turn the model would read back as instructions.
          const consumed = await consumePrompt(activeSession, prompt)
          // Crossing into draft-only while the command was applying cancels the
          // submission, exactly as it does for a turn that would be dispatched.
          if (draftOnlyRef.current) return false
          if (!consumed) {
            const hasActiveTurn = activeSession.view.getSnapshot().state?.activeTurn !== undefined
            dispatchKind = hasActiveTurn ? 'steer' : 'direct'
            const promptForDispatch = preparePromptForDispatch(prompt, dispatchKind)
            dispatchedPrompt = promptForDispatch
            const prepareDispatch = beforeDispatch
              ? () => Promise.resolve(beforeDispatch(activeSession, promptForDispatch))
              : undefined
            if (hasActiveTurn) {
              if (prepareDispatch) {
                dispatchReceipt = await activeSession.input.steer(promptForDispatch, undefined, prepareDispatch)
              } else {
                dispatchReceipt = await activeSession.input.steer(promptForDispatch)
              }
            } else if (prepareDispatch) {
              dispatchReceipt = await activeSession.input.submit(promptForDispatch, undefined, prepareDispatch)
            } else {
              dispatchReceipt = await activeSession.input.submit(promptForDispatch)
            }
          }
        } else if (initialSubmission) {
          interaction?.dependencyChanged('session', true)
          const metadata = includeDraftAttachments ? draftSubmissionMetadata : submissionMetadata
          dispatchKind = 'initial'
          dispatchedPrompt = preparePromptForDispatch(prompt, dispatchKind)
          onInitialSubmissionStarted?.(dispatchedPrompt)
          const canStopPendingRecovery =
            initialSubmissionOrigin === 'recovery' && initialSubmission.cancel !== undefined
          if (canStopPendingRecovery) setPendingAutoRecoverySubmission(true)
          const session = await initialSubmission
            .submit({
              prompt: dispatchedPrompt,
              origin: initialSubmissionOrigin,
              ...(Object.keys(metadata).length > 0 ? {metadata} : {}),
              ...(initialSessionMode ? {mode: initialSessionMode} : {}),
              ...(initialSessionApprovalMode ? {approvalMode: initialSessionApprovalMode} : {}),
              configureSession,
              ...(prepareInitialPrompt ? {preparePrompt: prepareInitialPrompt} : {}),
            })
            .finally(() => {
              if (pendingAutoSubmissionRef.current === pendingAutoSubmission && canStopPendingRecovery) {
                setPendingAutoRecoverySubmission(false)
              }
            })
          publishEstablishedSession(session)
          // The first message is what establishes the session, so a permission
          // command cannot be consumed before there is a session to configure.
          // Apply it once there is, so the mode still takes effect.
          await consumePrompt(session, dispatchedPrompt)
        } else {
          return false
        }
        if (dispatchKind && dispatchedPrompt) handleAutoTierDispatchAccepted(dispatchedPrompt, dispatchKind)
        if (pendingAutoSubmission && pendingAutoSubmissionRef.current !== pendingAutoSubmission) return true
        if (includeDraftAttachments) {
          settleAccepted(dispatchReceipt)
        } else {
          clearInjected(injectedIds)
          pendingAutoInjectedIdsRef.current.ids = []
          if (!isInitialSubmission) onSubmitSuccess?.(dispatchReceipt)
        }
        return true
      }
      // Settling has to happen on both outcomes, but the compiler cannot lower a
      // `finally` clause, nor a `throw` inside a `catch`, and either one leaves
      // this whole callback unoptimized. So the try/catch only records the
      // outcome, and the shared settle and the rethrow happen after it.
      let outcome: {ok: true; submitted: boolean} | {ok: false; error: unknown}
      try {
        outcome = {ok: true, submitted: await submit()}
      } catch (error) {
        outcome = {ok: false, error}
      }
      // Read before settling, which clears the ref this comparison reads.
      const superseded =
        pendingAutoSubmission !== undefined && pendingAutoSubmissionRef.current !== pendingAutoSubmission
      if (pendingAutoSubmission && pendingAutoSubmissionRef.current === pendingAutoSubmission) {
        pendingAutoSubmissionRef.current = undefined
        onInitialSubmissionSettled?.()
      }
      if (outcome.ok) {
        if (outcome.submitted) clientInteractionAccepted(interaction)
        else interaction?.unresolved('cancelled')
        return outcome.submitted
      }
      if (superseded) {
        interaction?.unresolved('superseded')
        return true
      }
      clientInteractionFailed(interaction)
      throw outcome.error
    },
    [
      clearInjected,
      configureSession,
      consumePrompt,
      consumeSessionlessPrompt,
      clearSubmitError,
      beforeDispatch,
      draftOnly,
      isBlockedByChatQuota,
      draftSubmissionMetadata,
      establishedSession,
      scopeOwner,
      scopeRevision,
      establishedSessionHandoff,
      handleAutoTierDispatchAccepted,
      initialSubmission,
      initialSubmissionOrigin,
      prepareInitialPrompt,
      initialSessionMode,
      initialSessionApprovalMode,
      preparePromptForDispatch,
      submissionMetadata,
      onInitialSubmissionSettled,
      onInitialSubmissionStarted,
      publishEstablishedSession,
      onSubmitSuccess,
      settleAccepted,
    ],
  )

  const submitAutoPrompt = useCallback(
    async (text: string, includeDraftAttachments = false, event?: Event): Promise<boolean> => {
      const interaction = clientInteractionForEvent(event)
      if (draftOnly) {
        interaction?.failed('handler_rejected')
        return false
      }
      const trimmedText = text.trim()
      if (!trimmedText) {
        interaction?.failed('handler_rejected')
        return false
      }
      if (await consumeSessionlessPrompt(trimmedText)) {
        interaction?.unresolved('handoff_unobserved')
        return true
      }
      interaction?.preparationStarted()
      clientInteractionPending(interaction)

      const isInitialSubmission = !establishedSession && Boolean(initialSubmission)
      if (isInitialSubmission) onInitialSubmissionStarted?.({text: trimmedText})
      const prompt = await preparePrompt(trimmedText, {includeDraftAttachments, ...(interaction ? {interaction} : {})})
      prepareClientInteraction(interaction, prompt)
      if (draftOnlyRef.current) {
        interaction?.unresolved('cancelled')
        if (isInitialSubmission) onInitialSubmissionSettled?.()
        return false
      }
      return submitPreparedPrompt(
        prompt,
        includeDraftAttachments,
        injected.map(item => item.token.id),
      )
    },
    [
      consumeSessionlessPrompt,
      draftOnly,
      establishedSession,
      initialSubmission,
      injected,
      onInitialSubmissionSettled,
      onInitialSubmissionStarted,
      preparePrompt,
      submitPreparedPrompt,
    ],
  )

  useEffect(() => {
    if (draftOnly || !initialPrompt || initialPromptDispatchedRef.current) return
    initialPromptDispatchedRef.current = true
    initialPromptRetryRef.current = initialPrompt
    pendingInitialValueRef.current = initialPrompt.text
    setValue('')
    onInitialPromptDispatched?.()
    void (async () => {
      try {
        const submitted = await submitPreparedPrompt(initialPrompt, true, [])
        if (!submitted && mountedRef.current) {
          setValue(initialPrompt.text)
          restorePromptModel(initialPrompt)
        }
      } catch (error) {
        if (mountedRef.current) {
          restorePromptModel(initialPrompt)
          handleSubmitError(error)
        }
      }
    })()
  }, [
    draftOnly,
    handleSubmitError,
    initialPrompt,
    onInitialPromptDispatched,
    restorePromptModel,
    setValue,
    submitPreparedPrompt,
  ])

  usePendingAutoSubmit({
    readOnly,
    // A scoped conversation can briefly render before its owner resolves. Wait
    // rather than filtering out an injected reference and sending the prompt alone.
    ready:
      !disabled &&
      !isBlockedByChatQuota &&
      !draftOnly &&
      Boolean(establishedSession || initialSubmission) &&
      (scopeOwner !== null || allInjected.length === 0),
    submitPrompt: submitAutoPrompt,
  })

  useImperativeHandle(
    handleRef,
    () => ({
      addFiles: files => {
        if (!readOnly && !disabled) void commonAttachments.handleFilesAdded(files, 'drop')
      },
    }),
    [commonAttachments, readOnly, disabled],
  )

  const attachmentPreview = imagePreview(commonAttachments, readOnly, activateReference)
  const {closeFilePicker, closeRepoPicker, setPreviewImage} = commonAttachments
  useLayoutEffect(() => {
    if (!hidden) return
    closeFilePicker()
    closeRepoPicker()
    setPreviewImage(null)
  }, [closeFilePicker, closeRepoPicker, hidden, setPreviewImage])
  // The Space token is a display-only scope indicator, not submitted content, so
  // it must not make an otherwise-empty message submittable.
  const hasAttachedContent =
    hasBlocks || hasSubmittableAttachments(composerAttachments) || commonAttachments.images.length > 0
  const isStreaming = isStreamingOverride ?? establishedView.snapshot.state?.activeTurn !== undefined
  const autoTierPicker: AutoTierPickerConfig | undefined = autoTierSupported
    ? {
        effectiveTier: effectiveAutoTier,
        selectedTier: selectedAutoTier,
        pendingTier: visiblePendingAutoTier,
        error: autoTierError?.context === autoTierErrorContext ? autoTierError.message : undefined,
        disabled: isStreaming || visiblePendingAutoTier !== undefined,
        onTierSelected: handleAutoTierSelected,
      }
    : undefined
  const handleSelectSuggestion = useCallback(
    async (suggestion: {prompt?: string; question: string}, event?: Event) => {
      try {
        await submitAutoPrompt((suggestion.prompt ?? suggestion.question).trim(), true, event)
      } catch (error) {
        clientInteractionFailed(clientInteractionForEvent(event))
        handleSubmitError(error)
      }
    },
    [handleSubmitError, submitAutoPrompt],
  )
  const latestExplicitAttachment =
    pastedReferences.at(-1)?.ahp ?? injected.at(-1)?.ahp ?? commonAttachments.tracked.at(-1)?.ahp
  const suggestionContext = latestExplicitAttachment
    ? suggestionContextForAttachment(latestExplicitAttachment)
    : commonAttachments.selectedRepos.length > 0
      ? 'repository'
      : suggestionContextForAttachment(activeImplicitWithoutPasted.at(-1))
  const handleInitialSubmissionStarted = useCallback(
    (prompt: PreparedPrompt) => {
      if (pendingInitialValueRef.current === null) pendingInitialValueRef.current = value
      setValue('')
      onInitialSubmissionStarted?.(prompt)
    },
    [onInitialSubmissionStarted, setValue, value],
  )
  const handleStopped = useCallback(() => {
    if (establishedSession || !initialSubmission) return
    pendingAutoSubmissionRef.current = undefined
    setPendingAutoRecoverySubmission(false)
    clearInjected(pendingAutoInjectedIdsRef.current.ids)
    pendingAutoInjectedIdsRef.current.ids = []
    settleAccepted()
    onInitialSubmissionStopped?.()
  }, [clearInjected, establishedSession, initialSubmission, onInitialSubmissionStopped, settleAccepted])

  const submitComposerCommand = (
    inputValue: string,
    submit: (event?: Event) => void,
    queue: boolean,
    event?: Event,
  ) => {
    if (draftOnly) {
      clientInteractionForEvent(event)?.failed('handler_rejected')
      return
    }
    // Staff debug commands stay on the client and never become chat turns.
    if (runStaffDebugCommand(inputValue)) {
      clientInteractionForEvent(event)?.unresolved('handoff_unobserved')
      consumedStaffCommandRef.current = inputValue
      setValue('')
      return
    }
    if (!queue && !establishedSession && initialSubmission) handleInitialSubmissionStarted({text: value})
    submit(event)
  }

  const composerInput = (
    // The shell keys its own layout rules (a first-child composer drops its top
    // border) off this marker, because the class itself now lives in this
    // package's CSS module rather than the shell's.
    <div
      className={clsx(
        styles.Composer,
        presentation === 'embedded' && styles.ComposerEmbedded,
        presentation === 'inline' && styles.ComposerInline,
      )}
      data-chat-composer-region
      hidden={hidden}
    >
      {submitErrorMessage && (
        <Flash variant="danger" role="alert">
          {submitErrorMessage}
        </Flash>
      )}
      {commonAttachments.rejections.length > 0 && (
        <AttachmentErrorBanner
          ref={rejectionBannerRef}
          messages={hidden ? [] : commonAttachments.rejectionMessages}
          errorCode="attachment_rejected"
          variant="warning"
          title="Attachment upload warning"
          layout="compact"
          onDismiss={commonAttachments.dismissRejections}
        />
      )}
      {!readOnly && !draftOnly && (
        <EntitlementQuotaBanner
          notice={chatQuotaNotice}
          focusAfterDismiss={focusComposer}
          className={styles.QuotaNotice}
        />
      )}
      <EstablishedAhpSessionInput
        focusTargetRef={textAreaRef}
        directTurnTelemetryContext={directTurnTelemetryContext}
        directTurnModelId={model?.id}
        value={value}
        onValueChange={setValue}
        readOnly={readOnly}
        disabled={disabled || isBlockedByChatQuota}
        draftOnly={draftOnly}
        preparePrompt={preparePrompt}
        preparePromptForDispatch={preparePromptForDispatch}
        onInputMutation={prepareInitialSubmission}
        onPaste={handleComposerPaste}
        onSubmitAccepted={settleAccepted}
        onQueueAccepted={settleAccepted}
        onDispatchAccepted={handleAutoTierDispatchAccepted}
        renderActions={false}
        initialSubmission={initialSubmission}
        initialSubmissionOrigin={initialSubmissionOrigin}
        initialSubmissionMetadata={
          Object.keys(draftSubmissionMetadata).length > 0 ? draftSubmissionMetadata : undefined
        }
        configureSession={configureSession}
        consumePrompt={consumePrompt}
        consumeSessionlessPrompt={consumeSessionlessDraft}
        initialSubmissionPending={pendingAutoRecoverySubmission}
        getEstablishedSession={establishedSessionHandoff.get}
        onSessionEstablished={publishEstablishedSession}
        onInitialSubmissionStarted={handleInitialSubmissionStarted}
        onInitialSubmissionSettled={onInitialSubmissionSettled}
        onStopped={handleStopped}
        onError={handleSubmissionSettled}
        beforeSubmit={async () => {
          clearSubmitError()
          await beforeSubmit?.()
        }}
        beforeQueue={clearSubmitError}
        beforeDispatch={beforeDispatch}
        integrations={{
          attachmentController: commonAttachments,
          ...(repository ? {repository} : {}),
        }}
        renderInput={inputProps => (
          <>
            <ComposerFileAttachBridge
              attachments={commonAttachments}
              pastedReferences={pastedReferences}
              writable={!inputProps.readOnly && !inputProps.disabled && !attachmentsBusy}
            />
            <ChatPromptFrame
              onReadiness={onReadiness}
              interactionObserver={interactionObserver}
              onPresentation={onPresentation}
              value={inputProps.value}
              onValueChange={next => {
                const nextValue = typeof next === 'function' ? next(inputProps.value) : next
                inputProps.onValueChange(nextValue)
                onValueChange?.(nextValue)
                if (!inputProps.readOnly && nextValue.trim()) onResumeIntent?.()
                reconcileFromText(nextValue)
                setSubmitErrorMessage(undefined)
              }}
              onSubmit={() => submitComposerCommand(inputProps.value, inputProps.onSubmit, false)}
              onSubmitInteraction={(_prompt, event) =>
                submitComposerCommand(inputProps.value, inputProps.onSubmit, false, event)
              }
              onQueueSubmit={
                establishedSession
                  ? () => submitComposerCommand(inputProps.value, inputProps.onQueueSubmit, true)
                  : undefined
              }
              onQueueSubmitInteraction={
                establishedSession
                  ? (_prompt, event) => submitComposerCommand(inputProps.value, inputProps.onQueueSubmit, true, event)
                  : undefined
              }
              onStop={inputProps.canStop ? inputProps.onStop : undefined}
              onStopInteraction={inputProps.canStop ? inputProps.onStop : undefined}
              textAreaRef={textAreaRef}
              supportsShellCommands
              isStreaming={isStreaming || inputProps.canStop}
              queueOnModifierEnter={Boolean(establishedSession)}
              submitWhileStreaming
              submitWhileStreamingAriaLabel="Steer active turn"
              clearOnSubmit={false}
              disabled={inputProps.disabled || attachmentsBusy}
              submitDisabled={inputProps.submitDisabled || attachmentsBusy}
              preventSubmitKeysWhenDisabled={draftOnly && onResumeIntent !== undefined}
              onBlockedSubmit={() => {
                if (!inputProps.readOnly && !establishedView.passive && (value.trim() || hasAttachedContent))
                  onResumeIntent?.()
              }}
              queueSubmitDisabled={inputProps.submitDisabled || attachmentsBusy}
              readOnly={inputProps.readOnly}
              ariaDescribedBy={composerDescriptionId}
              placeholder={inputProps.placeholder}
              ariaLabel={inputProps.ariaLabel}
              onInputMutation={inputProps.onInputMutation}
              attachments={referenceActivation.attachments}
              attachmentPreview={attachmentPreview}
              hasAttachedContent={hasAttachedContent}
              onPaste={inputProps.onPaste}
              onFilesAdded={commonAttachments.handleFilesAdded}
              onAttachFiles={commonAttachments.openFilePicker}
              onUploadFiles={triggerUpload}
              onAttachRepo={commonAttachments.openRepoPicker}
              attachButtonRef={attachButtonRef}
              onRemoveAttachment={handleRemoveAttachment}
              onAttachmentClick={referenceActivation.onAttachmentClick}
              attachMenuSide={attachMenuSide}
              portalContainerName={portalContainerName}
              disableAutoFocus={disableAutoFocus}
              containerClassName={responsiveStyles.container}
              aboveInput={
                hasBlocks ? (
                  <PrettifiedBlocksList
                    blocks={blocks}
                    onRemove={onRemoveBlock}
                    onToggleCollapse={onToggleCollapse}
                    onCodeChange={onCodeChange}
                    readOnly={readOnly}
                  />
                ) : null
              }
              onEmptyBackspace={!readOnly && hasBlocks ? popLastBlock : undefined}
              renderInput={textarea => (
                <ChatReferenceAutocomplete
                  key={pickerScopeOwner ?? 'unresolved'}
                  scopeOwner={pickerScopeOwner}
                  repositoryLoader={repositoryLoader}
                  selectedRepos={commonAttachments.selectedRepos}
                  onUpdateRepos={commonAttachments.handleUpdateRepos}
                  onFilePickerSelect={commonAttachments.handleFilePickerSelect}
                  attachReferenceUrl={attachReferenceUrl}
                  portalContainerName={portalContainerName}
                  restrictionOwner={referenceRestrictionOwner}
                >
                  {textarea}
                </ChatReferenceAutocomplete>
              )}
              modelControls={{
                isLoading: isLoadingModels,
                onUpdateModel: handleModelUpdate,
                selectedModelId,
                isTokenBasedBillingEnabled,
                autoTierPicker,
                selectedReasoningEffort: reasoningEffortEnabled ? selectedReasoningEffort : undefined,
                onUpdateReasoningEffort,
                supportedReasoningEfforts: reasoningEffortEnabled ? supportedReasoningEfforts : undefined,
                leadingControl: sessionConfiguration?.modePicker,
                trailingControl: tokenUsage ? (
                  <SessionTokenUsageMeter
                    models={models}
                    state={establishedView.state}
                    persistedState={tokenUsage.persistedState}
                  />
                ) : (
                  <TokenUsageMeter models={models} />
                ),
                responsiveIconOnly: true,
              }}
              spaceControl={{
                selectedSpace,
                onSelectSpace: handleSelectSpace,
                isResolving: false,
                disabled: inputProps.disabled || Boolean(establishedSession),
                portalContainerName,
              }}
              toolbarClassName={responsiveStyles.pickers}
            />
          </>
        )}
      />
      {!readOnly ? (
        <SlashCommandAutocomplete
          textAreaRef={textAreaRef}
          enabled={Boolean(establishedSession || initialSubmission)}
          prompt={value}
          sessionCommands={establishedSession?.commands}
          onPromptChange={nextValue => {
            setValue(nextValue)
            onValueChange?.(nextValue)
            if (!readOnly && !establishedView.passive && nextValue.trim()) onResumeIntent?.()
          }}
        />
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept={uploadAcceptAttribute(sandboxUpload.available)}
        onChange={commonAttachments.handleFileInputChange}
        data-testid="file-upload-input"
      />
      {!hidden && (
        <FilePickerPanel
          key={`files:${pickerScopeOwner ?? 'unresolved'}`}
          scopeOwner={pickerScopeOwner}
          isOpen={commonAttachments.filePickerOpen}
          onClose={commonAttachments.closeFilePicker}
          onSelect={commonAttachments.handleFilePickerSelect}
          repositoryLoader={repositoryLoader}
        />
      )}
      {!hidden && (
        <RepoPicker
          key={`repos:${pickerScopeOwner ?? 'unresolved'}`}
          anchorRef={attachButtonRef}
          repositoryLoader={repositoryLoader}
          selectedRepos={commonAttachments.selectedRepos}
          onUpdateRepos={commonAttachments.handleUpdateRepos}
          triggerOpen={commonAttachments.repoPickerOpen}
          onClose={commonAttachments.closeRepoPicker}
          anchorSide="outside-top"
          subtitle={attachmentRepoPickerScopeSubtitle(pickerScopeOwner)}
          restrictionOwner={referenceRestrictionOwner}
        />
      )}
      {!hidden && commonAttachments.previewImage && (
        <ImagePreviewDialog
          src={commonAttachments.previewImage.dataUrl}
          title={commonAttachments.previewImage.name}
          alt={commonAttachments.previewImage.name}
          onClose={() => commonAttachments.setPreviewImage(null)}
        />
      )}
    </div>
  )

  if (emptyState === 'immersive' && !readOnly && !hidden) {
    return (
      <div className={styles.HomeEmptyState}>
        <div className={styles.HomeEmptyStateInner}>
          <div className={styles.HomeEmptyStateHero}>
            <CopilotAnimation animationType={CopilotAnimationType.Idle} loopAnimation size={64} />
          </div>
          {composerInput}
          <ChatIcebreakers
            suggestionContext={suggestionContext}
            onSelectSuggestion={handleSelectSuggestion}
            onCommit={interactionObserver ? event => interactionObserver.command('send', event) : undefined}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      {emptyState === 'embedded' && !readOnly && !hidden ? (
        <EmptyState
          suggestionContext={suggestionContext}
          onSelectSuggestion={handleSelectSuggestion}
          onCommit={interactionObserver ? event => interactionObserver.command('send', event) : undefined}
        />
      ) : null}
      {composerInput}
    </>
  )
}

function QueuedMessageText({text}: {text: string | undefined}) {
  if (!text) return <span>Message with attachments</span>
  return text.includes('```') ? <HighlightedUserText text={text} collapsible={false} /> : <span>{text}</span>
}

/**
 * Chat's established session surface. Queue state, confirmations, and
 * ask-user takeovers stay with the session boundary instead of the shell.
 */
export function AhpSessionComposer({
  state,
  queuedMessages,
  queueReadOnly = false,
  readOnly = false,
  draftOnly = false,
  initialSubmission,
  initialSubmissionOrigin = 'new',
  initialSubmissionMetadata,
  prepareInitialPrompt,
  initialSelectedSpace,
  onSessionEstablished,
  suppressQueuedMessages,
  takeoverEnabled,
  pendingPrompt: suppliedPendingPrompt,
  onPromptRespond: suppliedOnPromptRespond,
  isSubmittingPrompt: suppliedIsSubmittingPrompt,
  onFocusFallback,
  textAreaRef: suppliedTextAreaRef,
  confirmationTestId,
  promptTestId,
  initialValue,
  presentation = 'embedded',
  hidden = false,
  ...props
}: AhpSessionComposerProps) {
  const internalTextAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const textAreaRef = suppliedTextAreaRef ?? internalTextAreaRef
  const session = useOptionalEstablishedAhpSession()
  const [persistDrafts] = useState(() => isFeatureEnabled('copilot_chat_persist_session_drafts'))
  // An initial prompt is submitted on mount, which would empty and clear a restored draft.
  const draftId =
    persistDrafts && !props.initialPrompt
      ? (props.draftKey ?? sessionDraftId(session?.identity ?? props.ahpSession))
      : undefined
  const [value, setValue] = useState(() => readPersistedDraft(draftId, 'chat') || (initialValue ?? ''))
  const [appliedInitialValue, setAppliedInitialValue] = useState(initialValue)
  const initialValueChanged = initialValue !== appliedInitialValue
  if (initialValueChanged) {
    setAppliedInitialValue(initialValue)
    if (initialValue !== undefined) setValue(initialValue)
  }
  const [appliedDraftId, setAppliedDraftId] = useState(draftId)
  if (draftId !== appliedDraftId) {
    setAppliedDraftId(draftId)
    // A session that has just been established keeps what the user is typing,
    // or restores its draft if nothing was typed yet; switching away from an
    // established session must not carry its draft along.
    if (appliedDraftId !== undefined || !value)
      setValue(readPersistedDraft(draftId, 'chat') ?? (initialValueChanged ? (initialValue ?? '') : ''))
  }
  usePersistedDraft(draftId, 'chat', value, {flushOnLeave: persistDrafts})
  const view = useOptionalEstablishedAhpSessionViewState()
  const canonicalState = session ? view.state : state
  const activeConfirmation = draftOnly
    ? undefined
    : ((canonicalState?.activeTurn && !Array.isArray(canonicalState.activeTurn.responseParts)
        ? undefined
        : canonicalState
          ? getActivePendingToolConfirmation(canonicalState)
          : undefined) ?? (session ? getActiveSessionToolConfirmation(view.sessionState) : undefined))
  const {
    pendingPrompt: derivedPendingPrompt,
    handlePromptRespond: derivedHandlePromptRespond,
    isSubmittingPrompt: derivedIsSubmittingPrompt,
  } = usePendingPromptTakeover({
    state: canonicalState,
    activeConfirmation,
    establishedSession: session,
  })
  const pendingPrompt = draftOnly ? undefined : (suppliedPendingPrompt ?? derivedPendingPrompt)
  const handlePromptRespond = suppliedOnPromptRespond ?? derivedHandlePromptRespond
  const isSubmittingPrompt = suppliedIsSubmittingPrompt ?? derivedIsSubmittingPrompt
  const presentationReadOnly = readOnly || hidden

  const handleQueuedMessagesExpandedChange = useCallback((expanded: boolean) => {
    sendEvent('copilot_chat.activate', {
      target: 'QUEUED_MESSAGES',
      action: expanded ? 'expanded' : 'collapsed',
      chat_version: 'v2',
      mode: 'immersive',
    })
  }, [])
  const handleQueuedMessageRemoveStarted = useCallback(() => {
    sendEvent('copilot_chat.activate', {
      target: 'QUEUED_MESSAGE_REMOVE',
      action: 'click',
      chat_version: 'v2',
      mode: 'immersive',
    })
  }, [])
  const handleQueuedMessageSendNowStarted = useCallback(() => {
    sendEvent('copilot_chat.activate', {
      target: 'QUEUED_MESSAGE_SEND_NOW',
      action: 'click',
      chat_version: 'v2',
      mode: 'immersive',
    })
  }, [])

  return (
    <EstablishedAhpSessionSurface
      state={state}
      queuedMessages={queuedMessages}
      queueReadOnly={queueReadOnly || draftOnly}
      suppressQueuedMessages={suppressQueuedMessages}
      readOnly={presentationReadOnly}
      takeoverEnabled={takeoverEnabled && !draftOnly}
      activeConfirmation={activeConfirmation}
      pendingPrompt={
        pendingPrompt
          ? {
              toolCallId: pendingPrompt.toolCallId,
              toolName: pendingPrompt.toolName,
              arguments: pendingPrompt.arguments,
            }
          : undefined
      }
      onPromptRespond={handlePromptRespond}
      isSubmittingPrompt={isSubmittingPrompt}
      confirmationClassName={clsx(
        styles.Composer,
        presentation === 'embedded' && styles.ComposerEmbedded,
        presentation === 'inline' && styles.ComposerInline,
      )}
      promptClassName={clsx(
        styles.Composer,
        presentation === 'embedded' && styles.ComposerEmbedded,
        presentation === 'inline' && styles.ComposerInline,
      )}
      confirmationTestId={confirmationTestId ?? 'chat-composer-confirmation'}
      promptTestId={promptTestId ?? 'chat-composer-ask-user'}
      focusTargetRef={textAreaRef}
      onFocusFallback={onFocusFallback}
      onExpandedChange={handleQueuedMessagesExpandedChange}
      onRemoveStarted={handleQueuedMessageRemoveStarted}
      onSendNowStarted={handleQueuedMessageSendNowStarted}
      renderQueuedMessage={message => <QueuedMessageText text={message.message.text} />}
      queuedMessagesAriaLabel="Queued messages"
      fillAvailableSpace={Boolean(props.emptyState)}
    >
      <AhpSessionComposerInput
        {...props}
        presentation={presentation}
        readOnly={presentationReadOnly}
        draftOnly={draftOnly}
        hidden={hidden}
        initialSubmission={initialSubmission}
        initialSubmissionOrigin={initialSubmissionOrigin}
        initialSubmissionMetadata={initialSubmissionMetadata}
        prepareInitialPrompt={prepareInitialPrompt}
        initialSelectedSpace={initialSelectedSpace}
        onSessionEstablished={onSessionEstablished}
        textAreaRef={textAreaRef}
        value={value}
        setValue={setValue}
        draftId={draftId}
      />
    </EstablishedAhpSessionSurface>
  )
}

AhpSessionComposer.displayName = 'AhpSessionComposer'
